import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  Container, 
  Grid, 
  useTheme, 
  useMediaQuery, 
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import ShoppingCart from '../../components/ShoppingCart';
import { productAPI } from '../../services/api';
import { calculateTieredPrice } from '../../utils/tieredPricing';

const PlaceOrderNew = ({ currentUser, onOrderSubmit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  
  // Track quantities per product
  const [quantities, setQuantities] = useState({});

  // Fetch all products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        const response = await productAPI.getProducts({ status: 'ACTIVE' });
        
        if (response.success && response.data) {
          // Filter to only EPL products and transform them
          const eplProducts = response.data
            .filter(p => p.organization === 'EPL')
            .map(p => ({
              id: p.id,
              name: p.name,
              sku: p.sku,
              productId: p.id,
              basePrice: Number(p.basePrice) || 0,
              description: p.description || '',
              images: p.images && p.images.length > 0 
                ? p.images 
                : ['/UnisynHub/Assets/equicenta-card.png'],
              volume: p.volume || '',
              storageTemp: p.storageTemp || '',
              shelfLife: p.shelfLife || '',
              category: p.category || '',
              organization: p.organization || '',
            }));
          
          setProducts(eplProducts);
          
          // Initialize quantities for each product
          const initialQuantities = {};
          eplProducts.forEach(p => {
            initialQuantities[p.id] = 1;
          });
          setQuantities(initialQuantities);
        } else {
          throw new Error('Failed to fetch products');
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProductsError(error.message || 'Failed to load products');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const primaryColor = '#5c725c'; // equicent green

  const handleQuantityChange = (productId, change) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + change)
    }));
  };

  const handleAddToCart = (product) => {
    if (!product) return;

    const quantity = quantities[product.id] || 1;
    const newItem = {
      name: product.name,
      price: product.basePrice || 0,
      quantity: quantity,
      image: product.images && product.images.length > 0 ? product.images[0] : '/UnisynHub/Assets/equicenta-card.png',
      sku: product.sku,
      productId: product.productId || product.id || product.sku
    };

    // Check if item already exists in cart
    const existingItemIndex = cartItems.findIndex(item => item.sku === product.sku);

    if (existingItemIndex >= 0) {
      // Update quantity if item exists
      const updatedCartItems = [...cartItems];
      updatedCartItems[existingItemIndex].quantity += quantity;
      setCartItems(updatedCartItems);
    } else {
      // Add new item
      setCartItems([...cartItems, newItem]);
    }

    // Open cart sidebar
    setCartOpen(true);

    // Reset quantity to 1 for this product
    setQuantities(prev => ({
      ...prev,
      [product.id]: 1
    }));
  };

  const handleUpdateCartQuantity = (itemIndex, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemIndex);
      return;
    }

    const updatedCartItems = [...cartItems];
    updatedCartItems[itemIndex].quantity = newQuantity;
    setCartItems(updatedCartItems);
  };

  const handleRemoveFromCart = (itemIndex) => {
    const updatedCartItems = cartItems.filter((_, index) => index !== itemIndex);
    setCartItems(updatedCartItems);
  };

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/client/checkout', {
      state: { cartItems }
    });
  };

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 },
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {/* Top Cart Summary Bar */}
        {cartItems.length > 0 && (
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: primaryColor,
            color: 'white',
            py: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, md: 2 }
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 9 21.1 9 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5H5.21L4.27 3H1V2M17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 19 21.1 19 20 18.1 18 17 18Z" />
                </svg>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '14px', md: '16px' } }}>
                  {cartItems.reduce((total, item) => total + item.quantity, 0)} item{cartItems.reduce((total, item) => total + item.quantity, 0) !== 1 ? 's' : ''} in cart
                </Typography>
              </Box>

              {/* Item details - hidden on mobile */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                {cartItems.slice(0, 2).map((item, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '13px'
                  }}>
                    {item.name} × {item.quantity}
                  </Box>
                ))}
                {cartItems.length > 2 && (
                  <Typography sx={{ fontSize: '13px', opacity: 0.8 }}>
                    +{cartItems.length - 2} more
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' }
            }}>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '16px', md: '18px' } }}>
                Total: ${cartItems.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 } }}>
                <Button
                  variant="contained"
                  onClick={() => setCartOpen(true)}
                  sx={{
                    bgcolor: 'white',
                    color: primaryColor,
                    fontWeight: 600,
                    fontSize: { xs: '12px', md: '14px' },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: '20px',
                    '&:hover': { bgcolor: '#f8f9fa', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isMobile ? 'View' : 'View Cart'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCheckout}
                  sx={{
                    bgcolor: '#1a5a1a',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '12px', md: '14px' },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: '20px',
                    '&:hover': { bgcolor: '#0d4a0d', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Checkout
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Page Header */}
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
          <Typography sx={{
            fontSize: { xs: '28px', md: '36px' },
            fontFamily: 'Poppins',
            fontWeight: 700,
            color: '#1a1a1a',
            mb: 1
          }}>
            Order Products
          </Typography>
          <Typography sx={{
            fontSize: { xs: '14px', md: '16px' },
            fontFamily: 'Poppins',
            color: '#666',
            mb: 3
          }}>
            Select the products you need and add them to your cart
          </Typography>
        </Container>

        {/* Loading State */}
        {productsLoading && (
          <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: primaryColor, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading products...
            </Typography>
          </Container>
        )}

        {/* Error State */}
        {productsError && !productsLoading && (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {productsError}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ bgcolor: primaryColor }}
            >
              Retry
            </Button>
          </Container>
        )}

        {/* No Products State */}
        {!productsLoading && !productsError && products.length === 0 && (
          <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
            <Alert severity="info">
              No products available at this time. Please contact support.
            </Alert>
          </Container>
        )}

        {/* Products Grid */}
        {!productsLoading && !productsError && products.length > 0 && (
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, pb: 6 }}>
            <Grid container spacing={3}>
              {products.map((product) => (
                <Grid item xs={12} md={6} lg={4} key={product.id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      border: '1px solid #e5e7eb',
                      borderRadius: '16px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {/* Product Image */}
                    <Box sx={{
                      height: { xs: '200px', md: '240px' },
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '16px 16px 0 0',
                      overflow: 'hidden',
                      p: 2
                    }}>
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                      {/* Product Info */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography sx={{
                            fontSize: { xs: '18px', md: '20px' },
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            color: '#1a1a1a'
                          }}>
                            {product.name}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            label={product.sku} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#f0fdf4', 
                              color: primaryColor,
                              fontWeight: 500,
                              fontSize: '12px'
                            }} 
                          />
                          {product.volume && (
                            <Chip 
                              label={product.volume} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                fontSize: '12px',
                                fontWeight: 600,
                                borderColor: product.volume.includes('3.0') ? '#d97706' : '#6b7280',
                                color: product.volume.includes('3.0') ? '#d97706' : '#6b7280',
                                bgcolor: product.volume.includes('3.0') ? '#fef3c7' : 'transparent'
                              }}
                            />
                          )}
                          {product.volume && product.volume.includes('3.0') && (
                            <Chip 
                              label="Large Volume" 
                              size="small" 
                              sx={{ 
                                fontSize: '11px',
                                fontWeight: 600,
                                bgcolor: '#fbbf24',
                                color: 'white'
                              }}
                            />
                          )}
                        </Box>

                        <Typography sx={{
                          fontSize: '14px',
                          fontFamily: 'Poppins',
                          color: '#666',
                          lineHeight: 1.5,
                          mb: 2,
                          minHeight: { xs: 'auto', md: '42px' }
                        }}>
                          {product.description || 'Premium regenerative therapy for equine athletes'}
                        </Typography>
                      </Box>

                      {/* Price */}
                      <Typography sx={{
                        fontSize: { xs: '24px', md: '28px' },
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        color: primaryColor,
                        mb: 2
                      }}>
                        ${product.basePrice.toFixed(2)}
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      {/* Quantity & Add to Cart */}
                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                          {/* Quantity Selector */}
                          <Box sx={{
                            width: { xs: '100%', sm: '120px' },
                            height: '44px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '22px',
                            border: '1px solid #e6e6e6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Box
                              onClick={() => handleQuantityChange(product.id, -1)}
                              sx={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#e5e5e5' }
                              }}
                            >
                              <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                                <path d="M1 1H9" stroke="#333" strokeWidth="1.5" />
                              </svg>
                            </Box>

                            <Typography sx={{
                              width: '40px',
                              textAlign: 'center',
                              fontSize: '16px',
                              fontFamily: 'Poppins',
                              fontWeight: '600',
                              color: '#1a1a1a'
                            }}>
                              {quantities[product.id] || 1}
                            </Typography>

                            <Box
                              onClick={() => handleQuantityChange(product.id, 1)}
                              sx={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#e5e5e5' }
                              }}
                            >
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <path d="M5 1V9M1 5H9" stroke="#333" strokeWidth="1.5" />
                              </svg>
                            </Box>
                          </Box>

                          {/* Add to Cart Button */}
                          <Button
                            onClick={() => handleAddToCart(product)}
                            variant="contained"
                            fullWidth
                            sx={{
                              flex: { xs: 'none', sm: 1 },
                              height: '44px',
                              backgroundColor: primaryColor,
                              borderRadius: '22px',
                              color: 'white',
                              fontSize: '14px',
                              fontFamily: 'Poppins',
                              fontWeight: '600',
                              '&:hover': { backgroundColor: '#4a5f4a' },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Add to Cart
                          </Button>
                        </Box>

                        {/* Subtotal Display */}
                        {(() => {
                          const quantity = quantities[product.id] || 1;
                          // Calculate total quantity for products with the same volume
                          const totalQuantityForVolume = products
                            .filter(p => p.volume === product.volume)
                            .reduce((sum, p) => sum + (quantities[p.id] || 1), 0);
                          const tieredPrice = calculateTieredPrice(totalQuantityForVolume);
                          // Show bulk pricing label only if this product's volume group meets the threshold
                          const isBulk = totalQuantityForVolume >= 20;
                          return (
                            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                              <Typography sx={{
                                fontSize: '13px',
                                color: '#666',
                                fontFamily: 'Poppins'
                              }}>
                                Subtotal: <strong style={{ color: primaryColor }}>
                                  ${(tieredPrice * quantity).toFixed(2)}
                                </strong>
                                {isBulk && (
                                  <Chip 
                                    label="Bulk pricing" 
                                    size="small" 
                                    sx={{ 
                                      ml: 1,
                                      height: '18px',
                                      fontSize: '9px',
                                      backgroundColor: primaryColor,
                                      color: 'white'
                                    }} 
                                  />
                                )}
                              </Typography>
                              <Typography sx={{
                                fontSize: '11px',
                                color: '#999',
                                fontFamily: 'Poppins',
                                mt: 0.5
                              }}>
                                ${tieredPrice.toFixed(2)} per vial
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Benefits Section */}
            <Box sx={{
              mt: 6,
              p: { xs: 3, md: 4 },
              backgroundColor: '#f0fdf4',
              borderRadius: '16px',
              border: '1px solid #bbf7d0'
            }}>
              <Typography sx={{
                fontSize: { xs: '18px', md: '20px' },
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#166534',
                mb: 3,
                textAlign: 'center'
              }}>
                Your Client Benefits
              </Typography>
              <Grid container spacing={3}>
                {[
                  { icon: '🚚', text: 'Priority Next-Day Shipping', desc: 'Cryopreserved and shipped on dry ice' },
                  { icon: '💳', text: 'Flexible Payment Options', desc: 'Pay now with 5% discount or Net 60 terms' },
                  { icon: '📞', text: 'Dedicated Account Support', desc: 'Direct line to your account manager' },
                  { icon: '📦', text: 'Volume Discounts Available', desc: 'Save more when you order in bulk' }
                ].map((benefit, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '32px', mb: 1 }}>{benefit.icon}</Typography>
                      <Typography sx={{
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        color: '#166534',
                        mb: 0.5
                      }}>
                        {benefit.text}
                      </Typography>
                      <Typography sx={{
                        fontSize: '12px',
                        fontFamily: 'Poppins',
                        color: '#15803d'
                      }}>
                        {benefit.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        )}

        {/* Shopping Cart Sidebar */}
        <ShoppingCart
          isOpen={cartOpen}
          onClose={() => setCartOpen(false)}
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={handleCheckout}
        />
      </Box>
    </Box>
  );
};

export default PlaceOrderNew;
